import { HomepageBlock } from '@kizen/page-builder/viewer';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useBreakpoint } from 'app/spacing';
import TableScrollContainer from '__components/Tables/ScrollContainer';
import useExpandingVerticalScrollbar from 'hooks/useExpandingVericalScrollbar';
import styled from '@emotion/styled';
import ScrollFadeContainer from '__components/Kizen/ScrollFadeContainer';
import {
  publicBusinessConfigQuery,
  selectPhoneRegionOptions,
} from 'query/business';
import { countriesQuery as getCountriesQuery } from 'query/geography';
import { displayNamesQuery } from 'query/records';
import {
  getBusinessMergeFieldLookup,
  getBusinessVariablesMergeFieldLookup,
  getTeamMemberMergeFieldLookup,
  replaceMergeFields,
} from 'utility/merge-fields';
import { HTMLBlockWrapper } from './components';
import { useLogActivityContext } from 'ts-activities/LogActivityContext';

export const StyledScrollContainer = styled(TableScrollContainer)`
  flex-grow: 1;
  flex-shrink: 1;
  width: 100%;
  overflow-y: auto;
  height: 100%;

  & > div,
  & .ScrollChildren,
  & .ScrollChildren > div:first-child {
    width: 100%;
  }

  & .ScrollChildren > div:last-child {
    margin-left: -8px;
    right: 0;
    z-index: 16;
  }

  &
    .gradient-vertical-container
    .ScrollChildren
    .gradient-vertical-container
    > div:first-child
    > div {
    z-index: 10;
    pointer-events: none;
  }

  &
    .gradient-vertical-container
    .ScrollChildren
    .gradient-vertical-container
    > div:first-child {
    z-index: 10;
    pointer-events: none;
  }
`;

export const HTMLBlock = ({ builderContent, setContentHeight }: any) => {
  const [blockWidth, setBlockWidth] = useState(0);
  const { openLogActivityModal } = useLogActivityContext();

  if (!builderContent) {
    throw new Error('Invalid HTML Content for Block Viewer');
  }

  const [scrolled, setScrolled] = useState([]);

  const isMobile = useBreakpoint();

  const auth = useSelector((state: any) => state.authentication);

  const rolesQuery = useQuery(
    displayNamesQuery(
      { role_ids: auth.teamMember.roles },
      {
        select: (res) => {
          return res.results.roles.map((role) => role.display_name);
        },
      }
    )
  );

  const countriesQuery = useQuery(getCountriesQuery());

  const phoneRegionsQuery = useQuery(
    publicBusinessConfigQuery({
      select: selectPhoneRegionOptions,
    })
  );

  const isLoading =
    rolesQuery.isLoading ||
    countriesQuery.isLoading ||
    phoneRegionsQuery.isLoading;

  const textTransform = useMemo(() => {
    const lookup = {
      business: getBusinessMergeFieldLookup({
        business: auth.chosenBusiness,
        countries: countriesQuery.data,
        phoneRegions: phoneRegionsQuery.data,
      }),
      business_variable: getBusinessVariablesMergeFieldLookup(
        auth.chosenBusiness.business_variables
      ),
      team_member: getTeamMemberMergeFieldLookup({
        teamMember: auth.teamMember,
        roles: rolesQuery.data,
        phoneRegions: phoneRegionsQuery.data,
        business: auth.chosenBusiness,
      }),
    };

    return (content: string) => {
      return replaceMergeFields(content, lookup);
    };
  }, [auth, countriesQuery.data, phoneRegionsQuery.data, rolesQuery.data]);

  const wrapperRefHandler = (ref: HTMLDivElement) => {
    const rect = ref?.getBoundingClientRect();
    const width = rect?.width ?? 0;
    setBlockWidth(width);
  };

  const { containerProps } = useExpandingVerticalScrollbar();

  return (
    <HTMLBlockWrapper ref={wrapperRefHandler}>
      <StyledScrollContainer {...containerProps} onChangeScrolled={setScrolled}>
        <ScrollFadeContainer top bottom scrolled={scrolled}>
          {!isLoading && (
            <HomepageBlock
              key={JSON.stringify(builderContent) + blockWidth}
              data={builderContent}
              containerWidth={blockWidth}
              applyMobileBreakpoints={isMobile}
              setContentHeight={setContentHeight}
              textTransform={textTransform}
              onLogActivityButtonClick={(activityId: string) => {
                openLogActivityModal(activityId);
              }}
            />
          )}
        </ScrollFadeContainer>
      </StyledScrollContainer>
    </HTMLBlockWrapper>
  );
};
